import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, filter, map } from 'rxjs/operators';
import { ToastService } from '@app/Common/toast';
import { SimpleFormFlowField } from '@app/Models/SimpleFormFlowField';
import { TenantService } from '@app/Services';
import { FieldModalComponent } from '../../../Modals/field-modal.component';
import { Field, ValueIsProperty } from '../../../Models';
import { Patch } from '../../../Models/Patch';
import { ModuleService } from '../../../Services/module.service';
import { ParamMapService } from '../../../Services/param-map.service';
import { FieldService } from '../fields/field.service';
import { ListAliases } from '@app/Constants/ListAliases';
import { ListParameters, ListTypes } from '@app/Constants';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { TranslationPipe } from '../../../../../../Services/translation.pipe';
import { ListItemTypeFilter } from '../../ListItemTypeFilter';
import { FieldGroupFilter } from '../../../../../../FieldGroupFilter';

@Component({
    selector: 'aw-rules',
    templateUrl: './rules.component.html',
    styleUrls: ['./rules.component.scss'],
    imports: [MatFormField, MatLabel, MatSelect, FormsModule, MatOption, MatButton, MatIcon, MatCheckbox, MatMiniFabButton, NgClass, ExtendedModule, AsyncPipe, TranslationPipe, ListItemTypeFilter, FieldGroupFilter]
})
export class RulesComponent implements OnInit, OnDestroy {
    drivingField: Field = new Field();
    drivingFields: Field[] = [];
    listItemsCache: any = {};
    listValues: any[];
    fieldFilter = '';
    fieldForm: Field = new Field();
    fieldStateCache: any = {};
    moduleVersion = '';
    drivingAttributes: any[];
    drivingAttribute: string;
    menuToggled = false;
    isDisabled = false;
    filterColumns = false;

    private moduleId = '';
    private version = 1;
    private stepId = '';
    private selectedDrivingField = '';
    private getMultiFields = false;
    private moduleSubscription: Subscription;
    private drivingFieldSubscription: Subscription;
    private activatedRouteSub: Subscription;
    private tenantSubscription: Subscription;

    public constructor(
        private activatedRoute: ActivatedRoute,
        public moduleService: ModuleService,
        private tenantService: TenantService,
        public dialog: MatDialog,
        public toast: ToastService,
        private fieldService: FieldService,
        private router: Router,
        public paramMapService: ParamMapService
      ) {}

    ngOnInit(): void {
        this.moduleId = this.paramMapService.getParam(this.activatedRoute, 'moduleId');
        this.version = parseInt(this.paramMapService.getParam(this.activatedRoute, 'version'), 10);

        this.activatedRouteSub = this.activatedRoute.queryParamMap.subscribe(queryParams => {
            this.fieldFilter = queryParams.get('fieldFilter');
            this.selectedDrivingField = queryParams.get('drivingField');
            this.getMultiFields = (queryParams.get('getMultiFields') === 'true');
        });

        this.tenantSubscription = this.tenantService.getUpdatedListItem$().subscribe(() => {
            if (this.drivingField.ListAlias) {
                this.getListItems(false).subscribe();
            }
        });

        this.moduleSubscription = combineLatest(
            this.activatedRoute.queryParams,
            this.moduleService.module().pipe(
                filter(module => module !== null),
                distinctUntilKeyChanged('ModuleId')
            )
        ).subscribe(([queryParams, result]) => {
            this.stepId = queryParams.stepId;
            this.fieldFilter = queryParams.fieldFilter;
            if (result !== null) {
                if (this.getMultiFields === true) {
                    this.moduleService.getStepMultiFields(this.stepId);
                } else {
                    this.moduleService.getStepFields(this.stepId);
                }
            }
        });

        this.drivingFieldSubscription = this.fieldService.getDrivingFields().subscribe((fields) => {
            if (fields === null) {
                return;
            }
            this.drivingFields = fields;
            if (this.drivingFields.length > 0) {
              if (
                this.selectedDrivingField &&
                this.selectedDrivingField !== '' &&
                this.drivingFields.some(
                  (drivingField) =>
                    drivingField.FieldId === this.selectedDrivingField
                )
              ) {
                this.drivingField = this.drivingFields.find(
                  (x) => x.FieldId === this.selectedDrivingField
                );
              } else {
                this.drivingField = this.drivingFields[0];
              }
              this.getListItems().subscribe();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.moduleSubscription !== null) {
            this.moduleSubscription.unsubscribe();
        }
        if (this.drivingFieldSubscription !== null) {
            this.drivingFieldSubscription.unsubscribe();
        }
        if (this.activatedRouteSub !== null) {
            this.activatedRouteSub.unsubscribe();
        }
        if (this.tenantSubscription !== null) {
            this.tenantSubscription.unsubscribe();
        }
    }

    groupTrackBy(index, item) {
        return index;
    }

    listValueTrackBy(index, item) {
        return item.Value;
    }

    fieldTrackBy(index, item: Field) {
        return item.FieldId;
    }

    openFieldModalComponent(field: Field): void {
        const dialogRef = this.dialog.open(FieldModalComponent);
        dialogRef.componentInstance.setField(field);
        dialogRef.componentInstance.DialogTitle = this.prettifyLabel(field.FieldName);
    }

    manageListItems(): void {
        this.moduleService.editList(this.drivingField.ListParams[ListParameters.ListAlias], this.drivingField.FieldName)
    }

    getListItems(fromCache: boolean = true): Observable<void> {
        if (!fromCache) {
            this.listItemsCache[this.drivingField.ListParams[ListParameters.ListAlias]] = null;
        }
        const cacheVal = this.listItemsCache[this.drivingField.ListParams[ListParameters.ListAlias]];
        let itemsSource: Observable<any[]>;
        if (cacheVal) {
          itemsSource = new Observable<any>((items: any) =>
            items.next(cacheVal)
          );
        } else {
          itemsSource = this.tenantService
            .getListItems([
              ListTypes.AirswebList,
              this.drivingField.ListParams[ListParameters.ListAlias],
            ])
            .pipe(
              map((items) => items as any[])
            );
        }
        return itemsSource.pipe(
            map(items => {
                for (const i of items) {
                    i.shown = true;
                }
                return items;
            }),
            map((items: any) => {

                this.listValues = items;
                this.listItemsCache[this.drivingField.ListParams[ListParameters.ListAlias]] = items;
            })
        );
    }

    drivingFieldChange(): void {
        this.selectedDrivingField = this.drivingField.FieldId;
        this.router.navigate([], {
            queryParams: {
                drivingField: this.selectedDrivingField
            },
            queryParamsHandling: 'merge',
            skipLocationChange: false
        });
        this.getListItems().subscribe();
    }

    getVisibilityRuleState(field: Field, type: any): boolean {
        const cacheId = `${this.moduleVersion}${field.FieldId}${this.drivingField.FieldId}vis${type.Value}`;
        if (this.fieldStateCache[cacheId] === undefined) {
            const state = Field.getVisibilityRuleState(field, type, this.drivingField.FieldId);
            this.fieldStateCache[cacheId] = state;
            return state;
        }
        return this.fieldStateCache[cacheId];
    }

    getHideRuleState(field: Field, type: any): boolean {
        const cacheId = `${this.moduleVersion}${field.FieldId}${this.drivingField.FieldId}hid${type.Value}`;
        if (this.fieldStateCache[cacheId] === undefined) {
            const state = Field.getHideRuleState(field, type, this.drivingField.FieldId);
            this.fieldStateCache[cacheId] = state;
            return state;
        }
        return this.fieldStateCache[cacheId];
    }

    hasAnyHideRuleStates(field: Field): boolean {
        const returnVal = this.listValues.some(x => Field.getHideRuleState(field, x, this.drivingField.FieldId));
        return returnVal;
    }

    hasAnyVisibilityRuleStates(field: Field): boolean {
        const returnVal = this.listValues.some(x => Field.getVisibilityRuleState(field, x, this.drivingField.FieldId));
        return returnVal;
    }

    setFieldModified(field: Field): void {
        if (!field.IsCustomField) {
          field.Modified = true;
        }

        this.moduleService.updateField(
            field,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).subscribe();
    }

    prettifyLabel(lbl: string): string {
        let op: string;
        if (!lbl) {
            return '';
        }
        if (lbl.indexOf(':') > -1) {
            op = lbl.split(':')[1];
        } else {
            op = lbl;
        }

        return op.replace(/_/g, ' ');
    }

    async toggleVisibilityRule(field: Field, type: any): Promise<void> {
        if (this.getHideRuleState(field, type)) {
            this.toggleHideRule(field, type);
        }

        if (this.hasAnyHideRuleStates(field)) {
            return;
        }

        const isBooleanField = this.drivingField.ListParams[ListParameters.ListAlias] === ListAliases.GeneralYesNo;
        const cacheId = `${this.moduleVersion}${field.FieldId}${this.drivingField.FieldId}vis${type.Value}`;
        const newRules = Field.toggleVisibilityRuleState(field, type, this.drivingField);
        Object.assign(field, newRules);
        await this.applyRules(field.FieldId, {
            VisibilityRules: newRules.VisibleWhenRules,
            VisibilityOrRules: newRules.VisibleWhenOrRules,
        });
        this.fieldStateCache[cacheId] = undefined;
        this.setFieldModified(field);
    }

    async toggleMandatoryRule(field: Field, type: any): Promise<void> {
        const cacheId = `${this.moduleVersion}${field.FieldId}${this.drivingField.FieldId}man${type.Value}`;
        const newRules = Field.toggleMandatoryRuleState(field, type, this.drivingField);
        Object.assign(field, newRules);
        await this.applyRules(field.FieldId, {
            MandatoryWhenRules: newRules.MandatoryWhenRules,
            MandatoryWhenOrRules: newRules.MandatoryWhenOrRules
        });
        this.fieldStateCache[cacheId] = undefined;
        this.setFieldModified(field);
    }

    async toggleHideRule(field: Field, type: any): Promise<boolean> {
        if (this.getVisibilityRuleState(field, type)) {
            this.toggleVisibilityRule(field, type);
        }
        if (this.hasAnyVisibilityRuleStates(field)) {
            return false;
        }
        const isBooleanField = this.drivingField.ListParams[ListParameters.ListAlias] === ListAliases.GeneralYesNo;
        const cacheId = `${this.moduleVersion}${field.FieldId}${this.drivingField.FieldId}hid${type.Value}`;
        const newRules = Field.toggleHideRuleState(field, type, this.drivingField.FieldId, isBooleanField);
        Object.assign(field, newRules);
        await this.applyRules(field.FieldId, {
            HideWhenRules: newRules.HideWhenRules,
            HideWhenOrRules: newRules.HideWhenOrRules
        });
        this.fieldStateCache[cacheId] = undefined;
        this.setFieldModified(field);
        return false;
    }

    getMandatoryRuleState(field: Field, type: any): boolean {
        const cacheId = `${this.moduleVersion}${field.FieldId}${this.drivingField.FieldId}man${type.Value}`;
        if (this.fieldStateCache[cacheId] === undefined) {
            const state = Field.getMandatoryRuleState(field, type, this.drivingField.FieldId);
            this.fieldStateCache[cacheId] = state;
            return state;
        }
        return this.fieldStateCache[cacheId];
    }

    getDrivingAttributes(parentField: SimpleFormFlowField): void {
        if (parentField) {
            const field = this.moduleService.getField(parentField.StepId, parentField.ParentPropertyName);

            this.tenantService.getListAttributeDefinitions(field.ListAlias)
                .subscribe(res => {
                    this.drivingAttributes = res;
                });
        }
    }

    fieldRulesEditable(field: Field): boolean {
        return (field.FieldId !== this.drivingField.FieldId && !field.IsMandatory && !field.IsAlwaysHidden);
    }

    private applyRules(fieldId: string, newRules: { [key: string]: ValueIsProperty[] }): Promise<any> {
        const patch = Object
            .keys(newRules)
            .map<Patch>(k => ({
                Operation: 'Replace',
                PropertyPaths: [[
                    ...this.moduleService.fieldsPath,
                    fieldId,
                    'Control',
                    'Properties',
                    k
                ]],
                Parameters: {
                    Value: newRules[k]
                }
            }));

        return this.moduleService.applyPatch(this.tenantService.selectedTenant, this.moduleId, patch);
    }

}
